import {
  dayDateOf,
  lunchMealToCartId,
  tt,
  zonedWallTime,
  zonedWallTimeToUtcMs,
  type LunchWeek,
} from '@agos/shared'

import { cutoffOf, type OpeningHours } from '@agos/customer-core'

/**
 * Ordering for later: the wall time the customer picks, and whether the server will accept it.
 *
 * The conversion itself — a Bratislava wall time to an instant, across a clock change — lives in
 * `@agos/shared`, beside the formatters that pin the same zone. FB-C09 and its measurement are
 * documented there; what is left here is the checkout's own policy.
 */

/** What goes in `scheduledAt` — an instant the server can parse, or `null`. */
export function scheduledAtOf(date: string, time: string): string | null {
  const instant = zonedWallTimeToUtcMs(date, time)
  return Number.isNaN(instant) ? null : new Date(instant).toISOString()
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

function minutesOf(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (match === null) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/**
 * Why this time will be refused, in the server's own terms, or `''` when it will not be.
 *
 * A mirror of `RestaurantAvailability.requireWithinOpeningHours`, including the overnight case, which is
 * `!closes.isAfter(opens)` there and the kind of branch a rewrite drops. The point is that the customer
 * learns before submitting rather than by being turned away.
 */
export function preorderProblem(
  date: string,
  time: string,
  hours: OpeningHours | null,
  now: Date,
): string {
  const instant = zonedWallTimeToUtcMs(date, time)
  if (Number.isNaN(instant)) return tt('Vyberte dátum aj čas.')
  if (instant <= now.getTime()) return tt('Zvolený čas už bol.')

  // Unreadable hours are not a refusal here: the server's own check gives up quietly when it cannot
  // read them, and refusing on this side would be stricter than the rule being mirrored.
  if (hours === null) return ''
  if (hours.emergencyClosed === true) return tt('Reštaurácia je dočasne zatvorená.')

  const wall = zonedWallTime(instant)
  const day = hours.days?.[DAY_KEYS[wall.day] ?? '']
  if (day === undefined) return ''
  if (day.open !== true) return tt('Reštaurácia je v zvolený deň zatvorená - vyberte iný čas.')

  const opens = minutesOf(day.from)
  const closes = minutesOf(cutoffOf(day))
  if (opens === null || closes === null) return ''

  const overnight = closes <= opens
  const inside = overnight
    ? wall.minutes >= opens || wall.minutes <= closes
    : wall.minutes >= opens && wall.minutes <= closes
  if (!inside) {
    return `${tt('Čas doručenia musí byť v rámci otváracích hodín')} (${day.from} – ${cutoffOf(day)}).`
  }
  return ''
}

/**
 * The day a basket's daily-menu lines are for — FRONTEND.md §9.219.
 *
 * *"For ordering for another day except today, Časenka should be enabled by default and also should
 * be available only for that specific date and time."*
 *
 * **Read off the basket, not remembered from the menu screen.** A meal is added from a day and then
 * the customer walks away, adds a pizza and comes back; reading the lines at the checkout gives the
 * right answer to somebody who emptied and refilled their basket.
 *
 * **Today is not a preorder** — a lunch taken for today is an ordinary order, and turning *Časenka*
 * on for it would make somebody pick a time to receive food they are asking for now.
 *
 * **The furthest day wins.** A basket with Wednesday's soup and Thursday's main cannot be delivered
 * twice, and the later day is the one that can carry both; refusing the basket would punish a
 * customer for a combination the menu itself offered.
 */
export function lunchPreorderDate(
  lines: readonly { productId?: number }[],
  weeks: readonly LunchWeek[],
  today: string,
): string | null {
  const wanted = new Set(lines.map((line) => line.productId))
  let furthest: string | null = null

  for (const week of weeks) {
    for (const day of week.days ?? []) {
      const date = dayDateOf(week.startDate, day.dayKey)
      // An undated week sells today, so it pins nothing: inventing a date would schedule an order
      // for a day nobody chose.
      if (date === null || date <= today) continue
      if (!(day.meals ?? []).some((meal) => wanted.has(lunchMealToCartId(meal)))) continue
      if (furthest === null || date > furthest) furthest = date
    }
  }

  return furthest
}

/**
 * The window a preorder for a daily menu may be placed in — §9.219.
 *
 * The menu's own serving hours, not the shop's: a *Časenka* for 21:00 on a menu served 11:00–14:00
 * is an order the kitchen will not honour. Empty settings answer null rather than a made-up window.
 */
export function lunchServingWindow(
  settings: { timeFrom?: string; timeTo?: string } | null | undefined,
): { from: string; to: string } | null {
  const from = (settings?.timeFrom ?? '').trim()
  const to = (settings?.timeTo ?? '').trim()
  if (from === '' || to === '' || from >= to) return null
  return { from, to }
}
